import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { EventFormModal } from '@/components/EventFormModal';
import { ColleagueMultiSelect } from '@/components/ui/ColleagueMultiSelect';
import { useAuthStore } from '@/stores/auth';
import { useRealtimeEvent, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import { eventsApi, EVENT_TYPE_LABELS, eventDotTone, type EventItem } from '@/services/events.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { cn, truncateAction } from '@/lib/utils';

type ViewMode = 'day' | 'week' | 'month';
type Scope =
  | { kind: 'mine' }
  | { kind: 'team'; teamId?: string }
  | { kind: 'users'; userIds: string[] };

const TONE_BG: Record<'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'purple', string> =
  {
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
    accent: 'bg-accent-50 text-accent-600 border-accent-200',
    success: 'bg-success-50 text-success-600 border-success-200',
    warning: 'bg-warning-50 text-warning-600 border-warning-200',
    danger: 'bg-danger-50 text-danger-600 border-danger-200',
    // Violet Stripe historique - appels.
    purple: 'bg-[#EEEDFF] text-[#5851DB] border-[#D9D6FF]',
  };

export function CalendarPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const [view, setView] = useState<ViewMode>('week');
  const [cursor, setCursor] = useState<Date>(new Date());
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [overdueCount, setOverdueCount] = useState<number>(0);
  // Refonte 2026-05-22 : 2e badge pour les RDV en retard, en orange
  // (warning), même design que le badge tâches en retard (danger). Source
  // backend dédiée /events/meetings/overdue, pas dérivable de la grille
  // calendrier (fenêtre temporelle limitée).
  const [overdueMeetingsCount, setOverdueMeetingsCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  // Filtres URL - synchronisés avec les deep-links :
  //  - `?filter=tasks|meetings|all` → type d'events affichés
  //  - `?scope=mine|team` → scope initial (la cloche ouvre en `mine`, la
  //    sidebar en `team`). Le scope `users` (collègues) reste géré en local.
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = (searchParams.get('filter') ?? 'all') as 'all' | 'tasks' | 'meetings';
  const urlScope = searchParams.get('scope');

  // Le scope est initialisé depuis l'URL ; défaut = team (aucun query).
  // Les changements d'URL (navigation sidebar / cloche) re-synchronisent le
  // scope via l'effet dédié - nécessaire car le composant reste monté et
  // useState ne rejoue pas l'initializer lors d'une navigation interne.
  const [scope, setScope] = useState<Scope>(() =>
    urlScope === 'mine' ? { kind: 'mine' } : { kind: 'team' },
  );
  useEffect(() => {
    if (urlScope === 'mine') setScope({ kind: 'mine' });
    else if (urlScope === 'team') setScope({ kind: 'team' });
    // Pas de query (`urlScope === null`) ou valeur inconnue → on laisse le
    // state tel quel pour ne pas écraser un changement utilisateur (toggle
    // "Collègues" notamment).
  }, [urlScope]);

  function setTypeFilter(v: 'all' | 'tasks' | 'meetings') {
    const next = new URLSearchParams(searchParams);
    if (v === 'all') next.delete('filter');
    else next.set('filter', v);
    setSearchParams(next, { replace: true });
  }

  const range = useMemo(() => rangeFor(view, cursor), [view, cursor]);

  useEffect(() => {
    // Chargement unique des membres d'équipe pour peupler le multi-select.
    let cancelled = false;
    usersApi
      .teamMembers(token)
      .then((list) => {
        if (!cancelled) setTeamMembers(list);
      })
      .catch(() => {
        if (!cancelled) setTeamMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function load() {
    setError(null);
    setEvents(null);
    try {
      const params = { from: range.from.toISOString(), to: range.to.toISOString() };
      if (scope.kind === 'mine') {
        const data = await eventsApi.myCalendar(token, params);
        setEvents(data);
      } else if (scope.kind === 'team') {
        const data = await eventsApi.teamCalendar(token, {
          ...params,
          ...(scope.teamId ? { team_id: scope.teamId } : {}),
        });
        setEvents(data.events);
      } else {
        // `users` : N appels parallèles à `userCalendar`, merge + dedupe par id.
        // Le backend vérifie le droit d'accès à chaque collègue (403 sinon).
        // Les erreurs individuelles ne bloquent pas l'ensemble - on affiche
        // ce qu'on a, et on log les échecs (rare : UUID révoqué depuis).
        const results = await Promise.allSettled(
          scope.userIds.map((uid) => eventsApi.userCalendar(token, uid, params)),
        );
        const byId = new Map<string, EventItem>();
        for (const r of results) {
          if (r.status !== 'fulfilled') continue;
          for (const ev of r.value.events) {
            if (!byId.has(ev.id)) byId.set(ev.id, ev);
          }
        }
        setEvents(Array.from(byId.values()));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  async function loadOverdue() {
    try {
      // Le badge "X tâches en retard" suit le scope actuel du calendar :
      //  - mine → tâches assignées à moi en retard
      //  - team → tâches en retard de toute l'équipe (scopeFilter applique le
      //           cloisonnement côté serveur)
      //  - users → tâches en retard des collègues sélectionnés
      const params: { scope: 'mine' | 'team' | 'users'; user_ids?: string[] } =
        scope.kind === 'mine'
          ? { scope: 'mine' }
          : scope.kind === 'team'
            ? { scope: 'team' }
            : { scope: 'users', user_ids: scope.userIds };
      const [tasks, meetings] = await Promise.all([
        eventsApi.overdueTasks(token, params),
        eventsApi.overdueMeetings(token, params),
      ]);
      setOverdueCount(tasks.length);
      setOverdueMeetingsCount(meetings.length);
    } catch {
      /* silencieux - badge informatif uniquement */
    }
  }

  useEffect(() => {
    void load();
    void loadOverdue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursor, scope]);

  // Realtime : une tâche / RDV complétés / créés / supprimés par soi ou
  // un collègue rafraîchit la grille calendrier + les 2 badges "en retard".
  useRealtimeEvent(REALTIME_EVENTS.TASK_MUTATED, () => {
    void load();
    void loadOverdue();
  });

  function shift(delta: number) {
    const d = new Date(cursor);
    if (view === 'day') d.setDate(d.getDate() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setCursor(d);
  }

  // Nom lisible des collègues sélectionnés pour le bandeau lecture-seule.
  const selectedUsersLabel = useMemo(() => {
    if (scope.kind !== 'users') return null;
    const names = scope.userIds
      .map((uid) => teamMembers.find((m) => m.id === uid))
      .filter((m): m is TeamMember => !!m)
      .map((m) => `${m.firstName} ${m.lastName}`);
    return names.join(', ');
  }, [scope, teamMembers]);

  return (
    <AppLayout>
      <PageHeader
        title="Calendrier"
        subtitle="Appels, rendez-vous et tâches planifiés."
        badges={
          (overdueCount > 0 || overdueMeetingsCount > 0) && (
            <>
              {overdueCount > 0 && (
                <span className="bg-danger-50 text-danger-600 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  {overdueCount} tâche{overdueCount > 1 ? 's' : ''} en retard
                </span>
              )}
              {/* Refonte 2026-05-22 : 2e badge RDV en retard, même design
                  que le badge tâches mais en orange (warning) — décision
                  produit distinction visuelle tâches=rouge / RDV=orange. */}
              {overdueMeetingsCount > 0 && (
                <span className="bg-warning-50 text-warning-600 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  {overdueMeetingsCount} RDV en retard
                </span>
              )}
            </>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="border-border-subtle inline-flex overflow-hidden rounded-md border bg-white">
          {(['day', 'week', 'month'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                view === v ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-brand',
              )}
              aria-pressed={view === v}
            >
              {v === 'day' ? 'Jour' : v === 'week' ? 'Semaine' : 'Mois'}
            </button>
          ))}
        </div>

        <div className="border-border-subtle inline-flex overflow-hidden rounded-md border bg-white">
          {(
            [
              { v: 'all', label: 'Tout' },
              { v: 'tasks', label: 'Tâches' },
              { v: 'meetings', label: 'RDV' },
            ] as const
          ).map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setTypeFilter(f.v)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                typeFilter === f.v
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-brand',
              )}
              aria-pressed={typeFilter === f.v}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="border-border-subtle inline-flex items-center gap-1 rounded-md border bg-white px-1 py-0.5">
          <button
            type="button"
            onClick={() => shift(-1)}
            className="text-text-muted hover:text-brand rounded px-1.5 py-1"
            aria-label="Précédent"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setCursor(new Date())}
            className="text-brand px-2 py-1 text-xs font-medium hover:underline"
          >
            Aujourd&apos;hui
          </button>
          <button
            type="button"
            onClick={() => shift(1)}
            className="text-text-muted hover:text-brand rounded px-1.5 py-1"
            aria-label="Suivant"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="text-text-muted ml-2 pr-2 text-xs">{rangeLabel(view, cursor)}</span>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="border-border-subtle inline-flex overflow-hidden rounded-md border bg-white">
            <button
              type="button"
              onClick={() => setScope({ kind: 'mine' })}
              className={cn(
                'px-3 py-1.5 text-xs font-medium',
                scope.kind === 'mine'
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-brand',
              )}
            >
              Mon calendrier
            </button>
            <button
              type="button"
              onClick={() => setScope({ kind: 'team' })}
              className={cn(
                'border-border-subtle border-l px-3 py-1.5 text-xs font-medium',
                scope.kind === 'team'
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:text-brand',
              )}
            >
              Équipe
            </button>
          </div>

          <ColleagueMultiSelect
            members={teamMembers}
            selected={scope.kind === 'users' ? scope.userIds : []}
            onChange={(ids) => {
              if (ids.length === 0) setScope({ kind: 'mine' });
              else setScope({ kind: 'users', userIds: ids });
            }}
          />

          {/* Bloc UUID équipe + bouton Filtrer masqué - la direction voit
              son équipe implicitement via scopeFilter serveur, le sélecteur
              d'équipe explicite sera ajouté en V2. */}
        </div>
      </div>

      {scope.kind === 'users' && selectedUsersLabel && (
        <Banner tone="info" className="mb-4">
          Lecture seule - Calendrier{scope.userIds.length > 1 ? 's' : ''} de {selectedUsersLabel}
        </Banner>
      )}

      {error && <Banner tone="danger">{error}</Banner>}

      {events === null && !error && <Skeleton className="h-[600px] w-full" />}

      {events !== null && (
        <Card className="p-0">
          {(() => {
            const filtered =
              typeFilter === 'tasks'
                ? events.filter((e) => e.type === 'task')
                : typeFilter === 'meetings'
                  ? events.filter((e) => e.type === 'meeting')
                  : events;
            return (
              <>
                {view === 'month' && (
                  <MonthGrid cursor={cursor} events={filtered} onOpen={setSelectedEvent} />
                )}
                {view === 'week' && (
                  <WeekGrid cursor={cursor} events={filtered} onOpen={setSelectedEvent} />
                )}
                {view === 'day' && (
                  <DayList cursor={cursor} events={filtered} onOpen={setSelectedEvent} />
                )}
              </>
            );
          })()}
        </Card>
      )}

      {selectedEvent && (
        <EventFormModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onChange={() => {
            void load();
            void loadOverdue();
          }}
        />
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Range calculators
// ---------------------------------------------------------------------------

function rangeFor(view: ViewMode, cursor: Date): { from: Date; to: Date } {
  const d = new Date(cursor);
  if (view === 'day') {
    const from = new Date(d);
    from.setHours(0, 0, 0, 0);
    const to = new Date(d);
    to.setHours(23, 59, 59, 999);
    return { from, to };
  }
  if (view === 'week') {
    const day = d.getDay() || 7; // Lundi=1 … Dimanche=7
    const from = new Date(d);
    from.setDate(from.getDate() - (day - 1));
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 7);
    to.setHours(23, 59, 59, 999);
    return { from, to };
  }
  // Mois - on étend au calendrier affiché (semaines complètes).
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const firstDow = first.getDay() || 7;
  const from = new Date(first);
  from.setDate(from.getDate() - (firstDow - 1));
  from.setHours(0, 0, 0, 0);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const lastDow = last.getDay() || 7;
  const to = new Date(last);
  to.setDate(to.getDate() + (7 - lastDow));
  to.setHours(23, 59, 59, 999);
  return { from, to };
}

function rangeLabel(view: ViewMode, cursor: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  if (view === 'day') {
    return cursor.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }
  if (view === 'week') {
    const r = rangeFor('week', cursor);
    const end = new Date(r.to);
    end.setDate(end.getDate() - 1);
    return `${fmt(r.from)} - ${fmt(end)}`;
  }
  return cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

function MonthGrid({
  cursor,
  events,
  onOpen,
}: {
  cursor: Date;
  events: EventItem[];
  onOpen: (ev: EventItem) => void;
}) {
  const r = rangeFor('month', cursor);
  const days: Date[] = [];
  const d = new Date(r.from);
  while (d < r.to) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  const byDay = groupByDay(events);
  const month = cursor.getMonth();
  return (
    <div>
      <div className="text-text-muted grid grid-cols-7 border-b text-center text-[11px] font-medium uppercase">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
          <div key={d} className="py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = keyFor(day);
          const inMonth = day.getMonth() === month;
          const isToday = isSameDay(day, new Date());
          const list = byDay.get(key) ?? [];
          return (
            <div
              key={key}
              className={cn(
                'min-h-[110px] border-b border-r p-1.5 text-left',
                !inMonth && 'bg-gray-50/70',
              )}
            >
              <div
                className={cn(
                  'mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium',
                  isToday ? 'bg-accent text-white' : inMonth ? 'text-brand' : 'text-text-muted',
                )}
              >
                {day.getDate()}
              </div>
              <ul className="space-y-0.5">
                {list.slice(0, 3).map((ev) => (
                  <li key={ev.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(ev)}
                      className={cn(
                        'w-full truncate rounded border px-1 py-0.5 text-left text-[10px]',
                        TONE_BG[eventDotTone(ev)],
                      )}
                      title={`${EVENT_TYPE_LABELS[ev.type]} · ${ev.creator.firstName} ${ev.creator.lastName}`}
                    >
                      {truncateAction(ev.title) || EVENT_TYPE_LABELS[ev.type]}
                    </button>
                  </li>
                ))}
                {list.length > 3 && (
                  <li className="text-text-muted pl-1 text-[10px]">+ {list.length - 3}</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Week grid - 7 colonnes avec événements empilés verticalement
// ---------------------------------------------------------------------------

function WeekGrid({
  cursor,
  events,
  onOpen,
}: {
  cursor: Date;
  events: EventItem[];
  onOpen: (ev: EventItem) => void;
}) {
  const r = rangeFor('week', cursor);
  const days: Date[] = [];
  const d = new Date(r.from);
  for (let i = 0; i < 7; i++) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  const byDay = groupByDay(events);
  return (
    <div className="grid grid-cols-7">
      {days.map((day) => {
        const key = keyFor(day);
        const list = (byDay.get(key) ?? []).sort(sortByTime);
        const isToday = isSameDay(day, new Date());
        return (
          <div key={key} className="min-h-[500px] border-r last:border-r-0">
            <div
              className={cn(
                'border-b px-3 py-2 text-center text-xs',
                isToday ? 'bg-accent/5 text-accent font-semibold' : 'text-text-muted',
              )}
            >
              {day.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })}
            </div>
            <ul className="space-y-1 p-2">
              {list.length === 0 && (
                <li className="text-text-muted py-4 text-center text-[11px]">-</li>
              )}
              {list.map((ev) => (
                <li key={ev.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(ev)}
                    className={cn(
                      'block w-full rounded border px-2 py-1 text-left text-[11px] transition-colors hover:brightness-95',
                      TONE_BG[eventDotTone(ev)],
                    )}
                  >
                    <div className="truncate font-medium">
                      {truncateAction(ev.title) || EVENT_TYPE_LABELS[ev.type]}
                    </div>
                    <div className="truncate text-[10px] opacity-80">
                      {timeOfEvent(ev)} · {ev.creator.firstName} {ev.creator.lastName}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day list - simple liste triée par heure
// ---------------------------------------------------------------------------

function DayList({
  cursor,
  events,
  onOpen,
}: {
  cursor: Date;
  events: EventItem[];
  onOpen: (ev: EventItem) => void;
}) {
  const sorted = [...events].sort(sortByTime);
  return (
    <div className="p-4">
      <h2 className="text-brand mb-4 text-sm font-semibold">
        {cursor.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </h2>
      {sorted.length === 0 && <p className="text-text-muted text-sm">Aucun événement ce jour.</p>}
      <ul className="space-y-2">
        {sorted.map((ev) => (
          <li key={ev.id}>
            <button
              type="button"
              onClick={() => onOpen(ev)}
              className={cn(
                'block w-full rounded border px-3 py-2 text-left text-sm transition-colors hover:brightness-95',
                TONE_BG[eventDotTone(ev)],
              )}
            >
              <div className="font-medium">
                {timeOfEvent(ev)} · {truncateAction(ev.title) || EVENT_TYPE_LABELS[ev.type]}
              </div>
              {ev.description && <div className="mt-0.5 text-xs opacity-80">{ev.description}</div>}
              <div className="mt-0.5 text-[11px] opacity-75">
                {ev.creator.firstName} {ev.creator.lastName}
                {ev.assignee &&
                  ev.assignee.id !== ev.creator.id &&
                  ` → ${ev.assignee.firstName} ${ev.assignee.lastName}`}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

function keyFor(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return keyFor(a) === keyFor(b);
}

function groupByDay(events: EventItem[]): Map<string, EventItem[]> {
  const m = new Map<string, EventItem[]>();
  for (const ev of events) {
    const iso = ev.scheduledAt ?? ev.taskDueDate ?? ev.createdAt;
    const k = keyFor(new Date(iso));
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(ev);
  }
  return m;
}

function sortByTime(a: EventItem, b: EventItem): number {
  const ta = new Date(a.scheduledAt ?? a.taskDueDate ?? a.createdAt).getTime();
  const tb = new Date(b.scheduledAt ?? b.taskDueDate ?? b.createdAt).getTime();
  return ta - tb;
}

function timeOfEvent(ev: EventItem): string {
  const iso = ev.scheduledAt ?? ev.taskDueDate ?? ev.createdAt;
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
